import { useState } from "react";

// 2.5.E.2: pacing-mode-radio-selektori. Mode-kohtaiset kuvaukset auttavat
// käyttäjää valitsemaan oikean asetuksen ilman, että tarvitsisi tietää
// concurrency/delay-arvoja.

export type PacingMode = "polite" | "balanced" | "fast";

const MODE_DESCRIPTIONS: Record<PacingMode, { title: string; description: string }> = {
  polite: {
    title: "Polite",
    description:
      "Hidas mutta näkymätön — 1 task kerrallaan, 200 ms väli. Loadavg-pause 4.0:lla. Default tavalliselle käytölle.",
  },
  balanced: {
    title: "Balanced",
    description:
      "Tasapainoinen — 2 tehtävää rinnan, 100 ms väli. Sopiva kun haluat näkyvää mutta hallittua etenemistä.",
  },
  fast: {
    title: "Fast",
    description:
      "Nopea backfill — 4 tehtävää rinnan, ei viivettä. Käytä kun haluat kuvat näkyviin pikaisesti (sopii kun kone ei ole työkuormassa).",
  },
};

export type PacingModeSelectorProps = {
  value: PacingMode;
  onChange: (next: PacingMode) => Promise<void> | void;
  disabled?: boolean;
};

export function PacingModeSelector({ value, onChange, disabled }: PacingModeSelectorProps) {
  const [busy, setBusy] = useState(false);

  async function handleChange(mode: PacingMode) {
    if (mode === value) return;
    setBusy(true);
    try {
      await onChange(mode);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      {(Object.entries(MODE_DESCRIPTIONS) as Array<[PacingMode, (typeof MODE_DESCRIPTIONS)[PacingMode]]>).map(
        ([mode, info]) => (
          <label
            key={mode}
            className={`flex cursor-pointer items-start gap-3 rounded border px-3 py-2 ${
              value === mode
                ? "border-cyan-600 bg-cyan-950/30"
                : "border-slate-700 bg-slate-900 hover:bg-slate-800"
            } ${disabled || busy ? "opacity-50" : ""}`}
          >
            <input
              type="radio"
              name="pacing-mode"
              value={mode}
              checked={value === mode}
              onChange={() => void handleChange(mode)}
              disabled={disabled || busy}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-100">{info.title}</div>
              <div className="mt-0.5 text-xs text-slate-400">{info.description}</div>
            </div>
          </label>
        )
      )}
    </div>
  );
}
